import type {
  TransferDestinationOption,
  TransferSourceOption,
} from 'features/feeBasedInstantTransfer/adapters/transferOptionsAdapter';
import {
  accountOptionAttributes,
  amountErrorFailure,
  ANALYTICS_SOURCES,
  blockingErrorFailure,
  selectionAttributes,
} from 'features/feeBasedInstantTransfer/helpers/analyticsFormatters';
import type { TransferAmountError } from 'features/feeBasedInstantTransfer/helpers/transferAmountRules';
import type { TransferBlockingError } from 'features/feeBasedInstantTransfer/helpers/transferBlockingError';
import useFailureTransition from 'features/feeBasedInstantTransfer/hooks/useFailureTransition';
import useTransferAnalytics from 'features/feeBasedInstantTransfer/hooks/useTransferAnalytics';
import {
  TRANSFER_INLINE_ERROR_ENCOUNTERED,
  TRANSFER_MONEY_ACCOUNT_SELECTED,
  TRANSFER_MONEY_FORM_COMPLETED,
  TRANSFER_MONEY_SELECT_ACCOUNT_CLICKED,
} from 'shared/analytics/events/transferMoney';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';

const AMOUNT_SCREEN_KEY = 'instantTransferAmount';

export type TransferDirection = 'from' | 'to';

interface TransferEntryAnalyticsState {
  blockingError: TransferBlockingError | undefined;
  hasNoSourceOptions: boolean;
  amountError: TransferAmountError | undefined;
  selectedSource: TransferSourceOption | undefined;
  selectedDestination: TransferDestinationOption | undefined;
  amount: string;
  feeRate: number | undefined;
}

export default function useTransferEntryAnalytics({
  blockingError,
  hasNoSourceOptions,
  amountError,
  selectedSource,
  selectedDestination,
  amount,
  feeRate,
}: TransferEntryAnalyticsState) {
  const { logTransferEvent, logFlowError } = useTransferAnalytics(
    ANALYTICS_SOURCES.amount,
  );
  const transferAttributes = selectionAttributes(
    selectedSource,
    selectedDestination,
    amount,
    feeRate,
  );

  // Logged on landing, before options load, so every arrival is counted whatever mode follows.
  useViewDidFocusScreenAnalyticsEvent({ key: AMOUNT_SCREEN_KEY });
  useFailureTransition(
    blockingError
      ? blockingErrorFailure(blockingError, hasNoSourceOptions)
      : undefined,
    (failure) => logFlowError('amount', failure, transferAttributes),
  );
  useFailureTransition(
    amountError ? amountErrorFailure(amountError) : undefined,
    ({ reason }) =>
      // The sheet defines this event without source_name, so it is left unset.
      logTransferEvent(TRANSFER_INLINE_ERROR_ENCOUNTERED, {
        ...transferAttributes,
        source_name: undefined,
        error_reason: reason,
      }),
  );

  const logSelectAccountClicked = (direction: TransferDirection) => {
    logTransferEvent(TRANSFER_MONEY_SELECT_ACCOUNT_CLICKED, { direction });
  };

  const logAccountSelected = (
    direction: TransferDirection,
    option: TransferSourceOption | TransferDestinationOption,
  ) => {
    logTransferEvent(TRANSFER_MONEY_ACCOUNT_SELECTED, {
      direction,
      ...accountOptionAttributes(option),
    });
  };

  const logFormCompleted = () => {
    logTransferEvent(TRANSFER_MONEY_FORM_COMPLETED, transferAttributes);
  };

  return { logSelectAccountClicked, logAccountSelected, logFormCompleted };
}
